import Button from 'components/Button'
import ErrorBlock from 'components/ErrorBlock'
import {
  Form,
  FormGroupContainer,
  FormRow,
  TextAreaContainer,
} from 'components/inputs'
import {
  CustomSelect,
  ItemSource,
  RenderSearchItemProps,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
} from 'components/ModalSheet'
import { observer } from 'mobx-react'
import React from 'react'
import TransferJobViewModel, {
  TransferJobOption,
  TransferType,
} from './TransferJobViewModel'
import { match } from 'utils/pattern-match-util'
import ClientInputEditor from './TransferToClient/index'
import ProInputEditor from './TransferToPro/index'
import ClientTeamInputEditor from './TransferToClientTeam'
import { useConfirmOnPageExit } from 'hooks/dom-hooks'

interface TransferJobDialogProps {
  vm: TransferJobViewModel
}

export default observer(function TransferJobDialog({
  vm,
}: TransferJobDialogProps) {
  const itemSource = React.useMemo(
    () => ItemSource.static(vm.filteredTransferOptions),
    [vm.filteredTransferOptions],
  )

  const selectProps = useCustomSelectStateFromItemSource({ itemSource })

  useConfirmOnPageExit(vm.transfer.pending)

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.transfer.pending ? undefined : vm.close}
      disableCloseOnEsc
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>
          Transfer {vm.jobs.length > 1 ? `${vm.jobs.length} Jobs` : 'Job'}
        </ModalSheetTitle>
      </ModalSheetHeader>

      <ModalSheetContent>
        <ModalSheetContentBlock expanded>
          <Form>
            <FormRow>
              <FormGroupContainer model={vm} field="option" label="Transfer to">
                <CustomSelect
                  {...selectProps}
                  value={vm.option}
                  itemToString={(i) => i.displayName}
                  multi={false}
                  onChange={vm.setTransferOption}
                  renderSearchItem={(itemProps) => (
                    <TransferSearchItem {...itemProps} />
                  )}
                  data-test="transfer-job-type-dropdown"
                />
              </FormGroupContainer>
            </FormRow>

            {match(vm.transferType, {
              [TransferType.Pool]: () => null,
              [TransferType.None]: () => null,
              [TransferType.Team]: () => (
                <ClientTeamInputEditor
                  vm={vm.transferViewModels[TransferType.Team]}
                />
              ),
              [TransferType.Pro]: () => (
                <ProInputEditor vm={vm.transferViewModels[TransferType.Pro]} />
              ),
              [TransferType.Client]: () => (
                <ClientInputEditor
                  vm={vm.transferViewModels[TransferType.Client]}
                />
              ),
            })}

            <FormRow>
              <TextAreaContainer
                model={vm}
                field="reason"
                name="reason"
                label="Reason"
                help={vm.showReasonTextBox}
                disabled={false}
                error={vm.validation.getError('reason')}
                data-test="transfer-job-reason-input"
                rows={1}
              />
            </FormRow>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      {vm.transfer.rejected && (
        <ErrorBlock tackOn="bottom" error={vm.transfer.error} />
      )}

      <ModalSheetFooter>
        <ModalSheetFooterButtonGroup>
          {vm.showRateProButton && vm.transferType === TransferType.Pro && (
            <Button
              type="default"
              onClick={vm.showProRatingDialog}
              data-test="transfer-job-rate-pro-button"
            >
              Rate Pro
            </Button>
          )}
        </ModalSheetFooterButtonGroup>

        <ModalSheetFooterSpacer />

        <ModalSheetFooterButtonGroup>
          <Button
            type="default"
            onClick={vm.close}
            data-test="transfer-job-close-button"
          >
            Close
          </Button>
          <Button
            type="primary"
            loading={vm.transfer.pending}
            onClick={vm.transfer}
            data-test="transfer-job-submit-button"
            disabled={
              vm.transfer.pending || vm.transferType === TransferType.None
            }
          >
            Transfer
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface TransferSearchItem extends RenderSearchItemProps<TransferJobOption> {}

const TransferSearchItem = React.forwardRef(
  (
    { item, itemToString, ...itemProps }: TransferSearchItem,
    ref: React.Ref<any>,
  ) => {
    return (
      <SearchResultItem
        ref={ref}
        {...itemProps}
        data-test={
          'transfer-job-type-option-' + item.transferType.toLowerCase()
        }
      >
        {item.displayName}
      </SearchResultItem>
    )
  },
)
